var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { deprecated } from '@ally/metronome-icons';
import { Dejargonator } from '../Dejargonator';
import { Icon } from '../Icon';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { get, propsToForward } from '../tools/utils';
import { a11y } from './strings';
const StyledContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: ${get.space('xxs')} ${get.space('xxs')} ${get.space('md')};
  position: relative;
`;
const StyledLabel = styled.label `
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: ${({ theme: { font } }) => font.body.sm.size};
  line-height: ${({ theme: { font } }) => font.body.sm.lineHeight};
  color: ${get.colors('slate-5')};
  margin-right: ${get.space('md')};

  :before {
    content: '';
    display: flex;
    align-self: flex-start;
    width: ${get.space('lg')};
    height: ${get.space('lg')};
    box-sizing: border-box;
    padding: ${get.space('xxs')};
    margin-right: ${get.space('sm')};
    background: ${get.colors('white')};
    border: 1px solid
      ${({ isSelected, isDisabled }) => {
    if (isDisabled) {
        return get.colors('periwinkle-300');
    }
    if (isSelected) {
        return get.colors('bluesuedeshoes');
    }
    return get.colors('periwinkle-500');
}};
    text-align: center;
    border-radius: 50%;
    flex-shrink: 0;
    z-index: 1;
  }

  ${({ isSelected, isDisabled, theme: { colors } }) => {
    if (isDisabled) {
        return `
      color: ${colors['gray-700']};
      :before {
        background: ${isSelected ? colors['periwinkle-300'] : colors.white};
        box-shadow: inset 0px 0px 0px 5px ${colors.white};
      }
    `;
    }
    if (isSelected) {
        return `
      :before {
        background: ${colors.bluesuedeshoes};
        box-shadow: inset 0px 0px 0px 5px ${colors.white};
      }
    `;
    }
    return '';
}}
`;
const StyledRadio = styled.input.withConfig({
    shouldForwardProp: propsToForward,
}) `
  position: absolute;
  cursor: pointer;
  height: ${get.space('lg')};
  width: ${get.space('lg')};
  box-sizing: border-box;
  top: 0;
  left: 0;
  background: none;
  border: none;
  appearance: none;

  &::-ms-check {
    color: transparent;
  }
`;
export const RadioButton = (_a) => {
    var { inputId, label, value, name, selectedOption, onChange, onBlur, index, selectionMade, className, disabled, contextualHelp } = _a, rest = __rest(_a, ["inputId", "label", "value", "name", "selectedOption", "onChange", "onBlur", "index", "selectionMade", "className", "disabled", "contextualHelp"]);
    const { current: uuid } = useRef(uuidv4());
    const { current: radioButtonId } = useRef(inputId || `radio-button-${uuid}`);
    const { current: srOnlyId } = useRef(`radio-button-sr-${uuid}`);
    const shouldSelect = selectedOption === value;
    // aria spec states that if no selection has been made, then the first radio
    // button in the group should have a tabindex of 0.
    const getTabIndex = () => {
        if ((!selectionMade && index === 0) || shouldSelect)
            return 0;
        return -1;
    };
    const tabIndex = getTabIndex();
    return (React.createElement(StyledContainer, null,
        React.createElement(StyledLabel, { className: className, htmlFor: radioButtonId, isSelected: selectedOption === value, isDisabled: !!disabled }, label),
        React.createElement(StyledRadio, Object.assign({ id: radioButtonId, type: "radio", value: value, name: name, checked: shouldSelect, onChange: onChange, onBlur: onBlur, "aria-checked": shouldSelect ? 'true' : 'false', tabIndex: tabIndex, disabled: disabled }, rest, (contextualHelp ? { 'aria-describedby': srOnlyId } : {}))),
        !!contextualHelp && (React.createElement(React.Fragment, null,
            React.createElement(Dejargonator, { trigger: Object.assign({ variant: 'icon', content: React.createElement(Icon, { icon: deprecated.muiHelpOutline, size: "md" }) }, ('topic' in contextualHelp
                    ? { topic: contextualHelp.topic }
                    : { ariaLabel: contextualHelp.ariaLabelTrigger })), heading: contextualHelp.heading, headingTag: contextualHelp.headingTag, body: contextualHelp.body, ariaLabelPopover: contextualHelp.ariaLabelPopover || a11y.ariaLabelPopover }),
            React.createElement(ScreenReaderOnly, { id: srOnlyId, "aria-hidden": true }, contextualHelp.screenReaderDescription)))));
};
//# sourceMappingURL=RadioButton.js.map